import { useEffect, useRef, useState } from 'react'
import { setStepUpHandler } from '../lib/stepUp'
import { apiRequest, msalInstance } from './msal'

interface Pending {
  claims: string
  message: string
  resolve: (token: string | null) => void
}

/**
 * Dialogen der beder om sikkerhedsnøglen (beslutning 092).
 *
 * Knappen åbner Microsofts vindue med claims-anmodningen. Conditional Access i Entra kræver dér en YubiKey og
 * "log ind hver gang" - Overblik ser aldrig nøglen, kun det token Microsoft udsteder bagefter.
 */
export function StepUpDialog() {
  const [pending, setPending] = useState<Pending | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const current = useRef<Pending | null>(null)

  useEffect(() => {
    setStepUpHandler(
      (claims, message) =>
        new Promise<string | null>((resolve) => {
          // Står der allerede en åben dialog, afvises den gamle - kun ét Microsoft-vindue ad gangen.
          current.current?.resolve(null)
          const next = { claims, message, resolve }
          current.current = next
          setError(null)
          setPending(next)
        })
    )
    return () => setStepUpHandler(null)
  }, [])

  if (!pending) return null

  function close(token: string | null) {
    pending!.resolve(token)
    current.current = null
    setPending(null)
    setBusy(false)
  }

  async function confirm() {
    if (!msalInstance) {
      setError('Sikkerhedsnøgle kræver login med Microsoft 365. Lokale brugere kan ikke bruge VPN.')
      return
    }
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
    setBusy(true)
    setError(null)
    try {
      // claims: nyt token under authentication context (Conditional Access kræver YubiKey).
      // prompt=login: Microsoft genbruger ikke en eksisterende session - nøglen skal bruges NU, hver gang.
      const result = await msalInstance.acquireTokenPopup({
        ...apiRequest,
        account,
        claims: pending!.claims,
        prompt: 'login',
        loginHint: account?.username
      })
      close(result.accessToken)
    } catch (err) {
      setBusy(false)
      const text = err instanceof Error ? err.message : ''
      setError(
        /popup_window_error|popup/i.test(text)
          ? 'Browseren blokerede Microsoft-vinduet. Tillad pop op-vinduer for flow.tc-el.dk og prøv igen.'
          : /user_cancelled/i.test(text)
            ? 'Godkendelsen blev afbrudt.'
            : 'Godkendelsen lykkedes ikke. Har du en registreret sikkerhedsnøgle i Microsoft 365?'
      )
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__card">
        <h2 style={{ margin: '0 0 10px', fontSize: 18 }}>🔑 Bekræft med sikkerhedsnøgle</h2>
        <p className="small" style={{ marginTop: 0 }}>{pending.message}</p>
        <ol className="small" style={{ paddingLeft: 18 }}>
          <li>Tryk "Bekræft med YubiKey". Et vindue fra Microsoft åbner.</li>
          <li>Sæt din YubiKey i (eller hold den mod telefonen), og tryk på den, når Microsoft beder om det.</li>
        </ol>
        {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={busy} onClick={() => void confirm()}>
            {busy ? 'Venter på Microsoft ...' : 'Bekræft med YubiKey'}
          </button>
          <button className="btn" disabled={busy} onClick={() => close(null)}>
            Annullér
          </button>
        </div>
      </div>
    </div>
  )
}
